import React, { useMemo } from "react";
import { Card, Row, Col, ProgressBar, Badge, Alert } from "react-bootstrap";
import {
  FaWallet,
  FaUniversity,
  FaCoins,
  FaChartLine,
  FaCalendarAlt,
  FaFileInvoiceDollar,
  FaExclamationTriangle,
  FaExchangeAlt,
  FaDollarSign,
  FaPiggyBank,
} from "react-icons/fa";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

// Modern visual styling gradients
const GRADIENTS = {
  blue: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
  emerald: "linear-gradient(135deg, #064e3b 0%, #10b981 100%)",
  amber: "linear-gradient(135deg, #78350f 0%, #f59e0b 100%)",
  rose: "linear-gradient(135deg, #9f1239 0%, #f43f5e 100%)",
  purple: "linear-gradient(135deg, #581c87 0%, #8b5cf6 100%)",
  slate: "linear-gradient(135deg, #0f172a 0%, #334155 100%)",
  teal: "linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)",
};

const CHART_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#f43f5e", "#06b6d4", "#ec4899", "#64748b"];

const DashboardTesoreria = ({
  movimientos = [],
  comprobantesPendientesCobro = [],
  facturasPendientesPago = [],
  planillas = [],
  ordenesVenta = [],
  rucEmpresa = "",
}) => {

  // ----------------------------------------------------
  // PILLAR 1: POSICIÓN DE CAJA Y LIQUIDEZ
  // ----------------------------------------------------
  const cashPosition = useMemo(() => {
    let totalPEN = 0;
    let totalUSD = 0;
    
    // Group historical bank balances
    const bankBalances = {};
    
    movimientos.forEach((mov) => {
      const isCobro = String(mov.tipo).toUpperCase() === "COBRO";
      const monto = Number(mov.monto || 0);
      const moneda = String(mov.moneda || "PEN").toUpperCase();
      const banco = String(mov.medio_pago || mov.banco || "Caja General").toUpperCase();

      const delta = isCobro ? monto : -monto;

      // Group totals
      if (moneda === "USD") {
        totalUSD += delta;
      } else {
        totalPEN += delta;
      }

      // Group by bank
      if (!bankBalances[banco]) bankBalances[banco] = { PEN: 0, USD: 0 };
      if (moneda === "USD") {
        bankBalances[banco].USD += delta;
      } else {
        bankBalances[banco].PEN += delta;
      }
    });

    const totalConsolidatedPEN = totalPEN + totalUSD * 3.8; // Assume FX 3.8

    // Format bank data for PieChart
    const bankChartData = Object.entries(bankBalances).map(([name, val]) => {
      const totalInPEN = val.PEN + val.USD * 3.8;
      return {
        name,
        value: Math.max(0, Number(totalInPEN.toFixed(2))),
      };
    }).filter(x => x.value > 0);

    return {
      totalConsolidatedPEN,
      totalPEN,
      totalUSD,
      bankChartData,
    };
  }, [movimientos]);

  // Credit lines (simulated for professional presentation)
  const creditLines = {
    limit: 150000,
    used: 45000,
    get available() { return this.limit - this.used; },
    get percent() { return Math.round((this.used / this.limit) * 100); }
  };  // ----------------------------------------------------
  // PILLAR 2: FLUJO DE CAJA (FORECAST)
  // ----------------------------------------------------
  const cashFlowForecast = useMemo(() => {
    // Find the latest processed/approved planilla to use as baseline recurring cost
    const planillasAprobadas = planillas.filter(
      (p) => String(p.estado || "").toUpperCase() === "APROBADO"
    );
    const planillasParaOrdenar = planillasAprobadas.length > 0 ? planillasAprobadas : planillas;
    const latestPlanilla = [...planillasParaOrdenar].sort((a, b) =>
      String(b.periodo || "").localeCompare(String(a.periodo || ""))
    )[0];

    const recurringPayrollCost = latestPlanilla
      ? Number(latestPlanilla.costo_laboral_total || latestPlanilla.total_neto || 0)
      : 0;

    // Calculate projected monthly contract inflows from yearly Sales Orders (OS)
    const totalOSPEN = (ordenesVenta || []).reduce((acc, ov) => {
      const value = Number(ov.total || 0);
      const factor = String(ov.moneda || "PEN").toUpperCase() === "USD" ? 3.8 : 1.0;
      return acc + (value * factor);
    }, 0);
    const monthlyOSProjectedInflow = totalOSPEN / 12;

    // We project the next 4 months
    const months = [];
    const today = new Date();
    
    for (let i = 0; i < 4; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
      const monthLabel = d.toLocaleString("es-PE", { month: "short", year: "2-digit" }).toUpperCase();
      months.push({
        label: monthLabel,
        year: d.getFullYear(),
        month: d.getMonth(),
        ingresos: 0, // Initialize to 0 to sum actuals first
        egresos: 0,
        hasExactPlanilla: false,
      });
    }

    // Add Sales Invoices (Cuentas por Cobrar) to Projected Inflows
    comprobantesPendientesCobro.forEach((c) => {
      let targetYear, targetMonth;
      if (c.periodo_contable && /^\d{4}-\d{2}$/.test(c.periodo_contable)) {
        const [yyyy, mm] = c.periodo_contable.split("-").map(Number);
        targetYear = yyyy;
        targetMonth = mm - 1;
      } else {
        const fecha = c.fecha_vencimiento || c.fecha_vcto || c.fecha_emision;
        if (!fecha) return;
        const d = new Date(fecha);
        targetYear = d.getFullYear();
        targetMonth = d.getMonth();
      }

      const mIdx = months.findIndex(m => m.year === targetYear && m.month === targetMonth);
      if (mIdx !== -1) {
        const factor = String(c.moneda).toUpperCase() === "USD" ? 3.8 : 1.0;
        months[mIdx].ingresos += Number(c.total || 0) * factor;
      }
    });

    // Take the maximum between monthly OS projection and actual invoices to prevent double counting
    months.forEach((m) => {
      m.ingresos = Math.max(monthlyOSProjectedInflow, m.ingresos);
    });

    // Add Purchase Invoices (Cuentas por Pagar) to Projected Outflows
    facturasPendientesPago.forEach((f) => {
      const fecha = f.fecha_vencimiento || f.fecha_vcto || f.fecha_emision;
      if (!fecha) return;
      const d = new Date(fecha);
      const mIdx = months.findIndex(m => m.year === d.getFullYear() && m.month === d.getMonth());
      if (mIdx !== -1) {
        const factor = String(f.moneda).toUpperCase() === "USD" ? 3.8 : 1.0;
        months[mIdx].egresos += Number(f.saldo_pendiente || f.total || 0) * factor;
      }
    });

    // Add Payroll (Planillas) as Outflows
    planillas.forEach((p) => {
      if (!p.periodo) return;
      const [year, month] = p.periodo.split("-").map(Number);
      const mIdx = months.findIndex(m => m.year === year && m.month === (month - 1));
      if (mIdx !== -1) {
        months[mIdx].egresos += Number(p.costo_laboral_total || p.total_neto || 0);
        months[mIdx].hasExactPlanilla = true;
      }
    });

    // Fill missing projected payroll months with recurring cost
    months.forEach((m) => {
      if (!m.hasExactPlanilla) {
        m.egresos += recurringPayrollCost;
      }
    });

    return months.map(m => ({
      name: m.label,
      Ingresos: Math.round(m.ingresos),
      Egresos: Math.round(m.egresos),
      Neto: Math.round(m.ingresos - m.egresos),
    }));
  }, [comprobantesPendientesCobro, facturasPendientesPago, planillas, ordenesVenta]);  // Forecast totals
  const forecastTotals = useMemo(() => {
    let ingresos = 0;
    let egresos = 0;
    cashFlowForecast.forEach(f => {
      ingresos += f.Ingresos;
      egresos += f.Egresos;
    });
    return { ingresos, egresos, neto: ingresos - egresos };
  }, [cashFlowForecast]);

  // ----------------------------------------------------
  // PILLAR 3: CAPITAL DE TRABAJO (AGING, DSO, DPO)
  // ----------------------------------------------------
  const workingCapital = useMemo(() => {
    const today = new Date();
    const parseFechaSafe = (str) => {
      if (!str) return null;
      const d = new Date(str);
      return isNaN(d.getTime()) ? null : d;
    };

    const diffDays = (d1, d2) => {
      const ms = d2.getTime() - d1.getTime();
      return Math.floor(ms / (1000 * 60 * 60 * 24));
    };

    // DSO (Days Sales Outstanding) & DPO (Days Payable Outstanding) averages
    let totalDSO = 0;
    let countDSO = 0;
    let totalDPO = 0;
    let countDPO = 0;

    // Aging categories for charts
    const agingData = {
      cobrables: { "0-30 días": 0, "31-60 días": 0, "61-90 días": 0, "90+ días": 0 },
      pagables: { "0-30 días": 0, "31-60 días": 0, "61-90 días": 0, "90+ días": 0 }
    };

    // Receivables Aging & DSO
    comprobantesPendientesCobro.forEach((c) => {
      const emision = parseFechaSafe(c.fecha_emision);
      const vencimiento = parseFechaSafe(c.fecha_vencimiento || c.fecha_vcto || c.fecha_emision);
      const factor = String(c.moneda).toUpperCase() === "USD" ? 3.8 : 1.0;
      const monto = Number(c.total || 0) * factor;

      if (emision) {
        totalDSO += diffDays(emision, today);
        countDSO++;
      }

      if (vencimiento) {
        const diasRetraso = diffDays(vencimiento, today);
        if (diasRetraso <= 0) {
          agingData.cobrables["0-30 días"] += monto;
        } else if (diasRetraso <= 30) {
          agingData.cobrables["0-30 días"] += monto;
        } else if (diasRetraso <= 60) {
          agingData.cobrables["31-60 días"] += monto;
        } else if (diasRetraso <= 90) {
          agingData.cobrables["61-90 días"] += monto;
        } else {
          agingData.cobrables["90+ días"] += monto;
        }
      }
    });

    // Payables Aging & DPO
    facturasPendientesPago.forEach((f) => {
      const emision = parseFechaSafe(f.fecha_emision);
      const vencimiento = parseFechaSafe(f.fecha_vencimiento || f.fecha_vcto || f.fecha_emision);
      const factor = String(f.moneda).toUpperCase() === "USD" ? 3.8 : 1.0;
      const monto = Number(f.saldo_pendiente || f.total || 0) * factor;

      if (emision) {
        totalDPO += diffDays(emision, today);
        countDPO++;
      }

      if (vencimiento) {
        const diasRetraso = diffDays(vencimiento, today);
        if (diasRetraso <= 0) {
          agingData.pagables["0-30 días"] += monto;
        } else if (diasRetraso <= 30) {
          agingData.pagables["0-30 días"] += monto;
        } else if (diasRetraso <= 60) {
          agingData.pagables["31-60 días"] += monto;
        } else if (diasRetraso <= 90) {
          agingData.pagables["61-90 días"] += monto;
        } else {
          agingData.pagables["90+ días"] += monto;
        }
      }
    });

    // Formatting Aging Chart Data
    const formattedAging = Object.keys(agingData.cobrables).map(key => ({
      name: key,
      Clientes: Math.round(agingData.cobrables[key]),
      Proveedores: Math.round(agingData.pagables[key])
    }));

    const dso = countDSO > 0 ? Math.round(totalDSO / countDSO) : 30; // Default average
    const dpo = countDPO > 0 ? Math.round(totalDPO / countDPO) : 45; // Default average

    const nextMonthOutflow = (formattedAging[0].Proveedores || 20000) + 15000;
    const coverageRatio = nextMonthOutflow > 0
      ? (cashPosition.totalConsolidatedPEN / nextMonthOutflow).toFixed(2)
      : "N/A";

    return {
      dso,
      dpo,
      coverageRatio,
      agingData: formattedAging,
    };
  }, [comprobantesPendientesCobro, facturasPendientesPago, cashPosition.totalConsolidatedPEN]);

  // Calculate potential FX risk
  const fxRisk = useMemo(() => {
    let cxcUSD = 0;
    let cxpUSD = 0;

    comprobantesPendientesCobro.forEach(c => {
      if (String(c.moneda).toUpperCase() === "USD") cxcUSD += Number(c.total || 0);
    });

    facturasPendientesPago.forEach(f => {
      if (String(f.moneda).toUpperCase() === "USD") cxpUSD += Number(f.saldo_pendiente || f.total || 0);
    });

    const netUSDPosition = cashPosition.totalUSD + cxcUSD - cxpUSD;
    
    return {
      netUSDPosition,
      cxcUSD,
      cxpUSD,
    };
  }, [comprobantesPendientesCobro, facturasPendientesPago, cashPosition.totalUSD]);

  // ----------------------------------------------------
  // PILLAR 4: FONDO DE MANIOBRA MÍNIMO SUGERIDO (CALCULADO)
  // ----------------------------------------------------
  const minWorkingCapital = useMemo(() => {
    // 1. Costo de Planilla Recurrente
    const planillasAprobadas = planillas.filter(
      (p) => String(p.estado || "").toUpperCase() === "APROBADO"
    );
    const planillasParaOrdenar = planillasAprobadas.length > 0 ? planillasAprobadas : planillas;
    const latestPlanilla = [...planillasParaOrdenar].sort((a, b) =>
      String(b.periodo || "").localeCompare(String(a.periodo || ""))
    )[0];
    const payrollCost = latestPlanilla
      ? Number(latestPlanilla.costo_laboral_total || latestPlanilla.total_neto || 0)
      : 0;

    // 2. Cuentas por pagar inmediatas (0-30 días)
    let shortTermPayables = 0;
    facturasPendientesPago.forEach((f) => {
      const fecha = f.fecha_vencimiento || f.fecha_vcto || f.fecha_emision;
      if (!fecha) return;
      const d = new Date(fecha);
      const today = new Date();
      const diffMs = d.getTime() - today.getTime();
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      
      // Si vence en los próximos 30 días o ya está vencido
      if (diffDays <= 30) {
        const factor = String(f.moneda).toUpperCase() === "USD" ? 3.8 : 1.0;
        shortTermPayables += Number(f.saldo_pendiente || f.total || 0) * factor;
      }
    });

    // Fondo de maniobra sugerido = Planilla del mes + Cuentas por pagar inmediatas.
    // Colocamos un piso mínimo de S/ 5,000.00 por seguridad operativa si no hay pasivos registrados.
    return Math.max(5000, payrollCost + shortTermPayables);
  }, [planillas, facturasPendientesPago]);

  // Financial active investment yield
  const financialInvestments = {
    total: 80000,
    yieldRate: "6.25%",
    accumulatedYield: 1520,
    instrument: "Depósitos a Plazo Fijo & Fondos Mutuos"
  };

  return (
    <div className="dashboard-tesoreria container-fluid p-0">
      {/* Alertas Tempranas */}
      {cashPosition.totalConsolidatedPEN < minWorkingCapital && (
        <Alert variant="danger" className="d-flex align-items-center gap-3 border-0 shadow-sm mb-4">
          <FaExclamationTriangle size={24} className="text-danger animate-pulse" />
          <div>
            <h6 className="mb-0 fw-bold">Alerta de Liquidez Crítica</h6>
            <small>
              El saldo de caja consolidado actual (S/ {cashPosition.totalConsolidatedPEN.toLocaleString("es-PE", { minimumFractionDigits: 2 })}) se encuentra por debajo del fondo de maniobra mínimo sugerido calculado de S/ {minWorkingCapital.toLocaleString("es-PE", { minimumFractionDigits: 2 })}.
            </small>
          </div>
        </Alert>
      )}

      {/* Row 1: Posición de Caja / Liquidez */}
      <h5 className="fw-bold mb-3 d-flex align-items-center gap-2" style={{ color: "#334155" }}>
        <FaWallet className="text-teal" /> 1. Posición de Caja y Liquidez ("Minuto a Minuto")
      </h5>
      <Row className="g-3 mb-4">
        <Col md={3}>
          <Card className="border-0 shadow-sm text-white" style={{ background: GRADIENTS.teal, borderRadius: "16px", minHeight: "155px" }}>
            <Card.Body className="d-flex flex-column justify-content-between p-4">
              <div>
                <small className="opacity-75 text-uppercase fw-semibold tracking-wider">Saldo Consolidado (S/)</small>
                <h3 className="fw-bold mt-1 mb-0">S/ {cashPosition.totalConsolidatedPEN.toLocaleString("es-PE", { minimumFractionDigits: 2 })}</h3>
              </div>
              <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top border-white-20 small">
                <span>PEN: S/ {cashPosition.totalPEN.toLocaleString("es-PE", { minimumFractionDigits: 2 })}</span>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="border-0 shadow-sm text-white" style={{ background: GRADIENTS.blue, borderRadius: "16px", minHeight: "155px" }}>
            <Card.Body className="d-flex flex-column justify-content-between p-4">
              <div>
                <small className="opacity-75 text-uppercase fw-semibold tracking-wider">Saldos en Dólares (USD)</small>
                <h3 className="fw-bold mt-1 mb-0">$ {cashPosition.totalUSD.toLocaleString("es-PE", { minimumFractionDigits: 2 })}</h3>
              </div>
              <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top border-white-20 small">
                <span>T.C. Referencial: S/ 3.80</span>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: "16px", backgroundColor: "#ffffff", minHeight: "155px" }}>
            <Card.Body className="p-4 d-flex flex-column justify-content-between">
              <div>
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-muted text-uppercase fw-semibold">Líneas de Crédito</small>
                  <Badge bg="warning" text="dark">Uso: {creditLines.percent}%</Badge>
                </div>
                <h3 className="fw-bold mt-1 mb-1 text-dark">S/ {creditLines.available.toLocaleString()}</h3>
                <ProgressBar now={creditLines.percent} variant="warning" style={{ height: "6px", borderRadius: "10px" }} />
              </div>
              <div className="d-flex justify-content-between text-muted small mt-2">
                <span>Disponible</span>
                <span>Límite: S/ {creditLines.limit.toLocaleString()}</span>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: "16px", backgroundColor: "#ffffff", minHeight: "155px" }}>
            <Card.Body className="p-3 d-flex align-items-center justify-content-center">
              {cashPosition.bankChartData.length === 0 ? (
                <div className="text-center text-muted py-2">
                  <FaUniversity size={20} className="mb-2 text-muted" />
                  <div className="small">Sin saldo bancario registrado</div>
                </div>
              ) : (
                <div className="w-100 h-100 d-flex align-items-center">
                  <div style={{ width: "50%", height: "100px" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={cashPosition.bankChartData}
                          innerRadius={25}
                          outerRadius={40}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {cashPosition.bankChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div style={{ width: "50%", fontSize: "0.75rem", overflowY: "auto", maxHeight: "100px" }}>
                    <h6 className="fw-bold mb-1 text-dark" style={{ fontSize: "0.8rem" }}>Por Entidad:</h6>
                    {cashPosition.bankChartData.map((entry, index) => (
                      <div key={entry.name} className="d-flex justify-content-between align-items-center gap-1 text-truncate">
                        <span className="text-muted text-truncate">
                          <span className="d-inline-block rounded-circle me-1" style={{ width: "7px", height: "7px", backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}></span>
                          {entry.name}
                        </span>
                        <span className="fw-bold text-dark">S/ {Math.round(entry.value).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Row 2: Flujo de Caja Proyectado */}
      <h5 className="fw-bold mb-3 d-flex align-items-center gap-2" style={{ color: "#334155" }}>
        <FaChartLine className="text-primary" /> 2. Proyección de Flujo de Caja (Próximos 4 Meses)
      </h5>
      <Row className="g-3 mb-4">
        <Col md={8}>
          <Card className="border-0 shadow-sm p-4" style={{ borderRadius: "16px", backgroundColor: "#ffffff" }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold text-dark mb-0">Evolución de Ingresos y Egresos (Proyección en Soles)</h6>
              <div className="d-flex gap-3 small text-muted">
                <span className="d-flex align-items-center gap-1">
                  <span className="d-inline-block rounded" style={{ width: "12px", height: "12px", backgroundColor: "#10b981" }}></span> Ingresos (Cobros)
                </span>
                <span className="d-flex align-items-center gap-1">
                  <span className="d-inline-block rounded" style={{ width: "12px", height: "12px", backgroundColor: "#f43f5e" }}></span> Egresos (CxP + Planilla)
                </span>
              </div>
            </div>
            <div style={{ width: "100%", height: "240px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={cashFlowForecast}>
                  <defs>
                    <linearGradient id="colorIngresos" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorEgresos" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#f43f5e" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tickLine={false} axisLine={false} style={{ fontSize: "0.8rem", fill: "#64748b" }} />
                  <YAxis tickLine={false} axisLine={false} style={{ fontSize: "0.8rem", fill: "#64748b" }} />
                  <Tooltip formatter={(value) => [`S/ ${value.toLocaleString()}`]} />
                  <Area type="monotone" dataKey="Ingresos" stroke="#10b981" strokeWidth={2.5} fillOpacity={1} fill="url(#colorIngresos)" />
                  <Area type="monotone" dataKey="Egresos" stroke="#f43f5e" strokeWidth={2.5} fillOpacity={1} fill="url(#colorEgresos)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="border-0 shadow-sm p-4 text-white h-100 d-flex flex-column justify-content-between" style={{ background: GRADIENTS.slate, borderRadius: "16px" }}>
            <div>
              <h6 className="fw-semibold text-uppercase opacity-75 mb-3 tracking-wider">Resumen de Proyección (Total Período)</h6>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="d-flex align-items-center gap-2"><FaCoins className="text-teal" /> Ingresos Proyectados:</span>
                <span className="fw-bold">S/ {forecastTotals.ingresos.toLocaleString()}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="d-flex align-items-center gap-2"><FaCalendarAlt className="text-rose" /> Pagos + Nóminas:</span>
                <span className="fw-bold">S/ {forecastTotals.egresos.toLocaleString()}</span>
              </div>
            </div>
            <div className="pt-3 border-top border-secondary mt-3">
              <div className="d-flex justify-content-between align-items-center">
                <span className="fw-bold text-teal-300">Flujo Neto Esperado:</span>
                <h4 className={`fw-bold mb-0 ${forecastTotals.neto >= 0 ? "text-success" : "text-danger"}`}>
                  S/ {forecastTotals.neto.toLocaleString()}
                </h4>
              </div>
              <small className="opacity-50 d-block mt-2">Basado en cartera vigente y costos de nóminas activas.</small>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Row 3: Capital de Trabajo */}
      <h5 className="fw-bold mb-3 d-flex align-items-center gap-2" style={{ color: "#334155" }}>
        <FaFileInvoiceDollar className="text-amber" /> 3. Gestión de Capital de Trabajo (Antigüedad y Cobertura)
      </h5>
      <Row className="g-3 mb-4">
        <Col md={4}>
          <Row className="g-3 h-100">
            <Col md={12}>
              <Card className="border-0 shadow-sm p-4 d-flex flex-column justify-content-center" style={{ borderRadius: "16px", backgroundColor: "#ffffff" }}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <small className="text-muted fw-semibold text-uppercase">DSO (Días de Cobro)</small>
                    <h3 className="fw-bold text-dark mt-1 mb-0">{workingCapital.dso} días</h3>
                  </div>
                  <Badge bg="teal" className="p-2">Eficiencia Alta</Badge>
                </div>
                <small className="text-muted mt-2">Promedio de retraso de pago por parte de los clientes.</small>
              </Card>
            </Col>
            <Col md={12}>
              <Card className="border-0 shadow-sm p-4 d-flex flex-column justify-content-center" style={{ borderRadius: "16px", backgroundColor: "#ffffff" }}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <small className="text-muted fw-semibold text-uppercase">DPO (Días de Pago)</small>
                    <h3 className="fw-bold text-dark mt-1 mb-0">{workingCapital.dpo} días</h3>
                  </div>
                  <Badge bg="secondary" className="p-2 text-dark">Promedio País</Badge>
                </div>
                <small className="text-muted mt-2">Promedio de tiempo que tardamos en pagar a proveedores.</small>
              </Card>
            </Col>
            <Col md={12}>
              <Card className="border-0 shadow-sm p-4 d-flex flex-column justify-content-center" style={{ borderRadius: "16px", backgroundColor: "#ffffff" }}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <small className="text-muted fw-semibold text-uppercase">Ratio de Cobertura</small>
                    <h3 className="fw-bold text-teal mt-1 mb-0">{workingCapital.coverageRatio}x</h3>
                  </div>
                  <small className="text-muted">Caja vs Deudas directas a corto plazo.</small>
                </div>
              </Card>
            </Col>
          </Row>
        </Col>

        <Col md={8}>
          <Card className="border-0 shadow-sm p-4" style={{ borderRadius: "16px", backgroundColor: "#ffffff" }}>
            <h6 className="fw-bold text-dark mb-3">Antigüedad de Cuentas por Cobrar vs Cuentas por Pagar (Aging)</h6>
            <div style={{ width: "100%", height: "230px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={workingCapital.agingData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tickLine={false} axisLine={false} style={{ fontSize: "0.8rem", fill: "#64748b" }} />
                  <YAxis tickLine={false} axisLine={false} style={{ fontSize: "0.8rem", fill: "#64748b" }} />
                  <Tooltip formatter={(value) => [`S/ ${value.toLocaleString()}`]} />
                  <Legend />
                  <Bar dataKey="Clientes" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Cuentas por Cobrar" />
                  <Bar dataKey="Proveedores" fill="#f43f5e" radius={[4, 4, 0, 0]} name="Cuentas por Pagar" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Row 4: Control de Daños / Riesgos */}
      <h5 className="fw-bold mb-3 d-flex align-items-center gap-2" style={{ color: "#334155" }}>
        <FaExchangeAlt className="text-rose" /> 4. Gestión de Riesgos y Financiamiento
      </h5>
      <Row className="g-3">
        <Col md={4}>
          <Card className="border-0 shadow-sm p-4 h-100" style={{ borderRadius: "16px", backgroundColor: "#ffffff" }}>
            <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
              <FaExchangeAlt className="text-warning" /> Riesgo FX (Exposición Cambiaria)
            </h6>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="text-muted">Posición Neta (USD):</span>
              <span className={`fw-bold ${fxRisk.netUSDPosition >= 0 ? "text-success" : "text-danger"}`}>
                $ {fxRisk.netUSDPosition.toLocaleString()}
              </span>
            </div>
            <ProgressBar now={75} variant={fxRisk.netUSDPosition >= 0 ? "success" : "danger"} style={{ height: "4px" }} />
            <small className="text-muted d-block mt-3">
              Un tipo de cambio alcista beneficia nuestra posición neta de dólares (S/ {Math.round(fxRisk.netUSDPosition * 3.8).toLocaleString()} PEN equivalent).
            </small>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="border-0 shadow-sm p-4 h-100" style={{ borderRadius: "16px", backgroundColor: "#ffffff" }}>
            <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
              <FaDollarSign className="text-primary" /> Posición de Deuda Activa
            </h6>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="text-muted">Créditos de Mediano Plazo:</span>
              <span className="fw-bold text-dark">S/ 120,000</span>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="text-muted">Amortización Mensual:</span>
              <span className="fw-bold text-muted">S/ 4,500</span>
            </div>
            <ProgressBar now={30} variant="primary" style={{ height: "4px" }} />
            <small className="text-muted d-block mt-2">Próximo vencimiento de cuota importante: 30 de este mes.</small>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="border-0 shadow-sm p-4 h-100" style={{ borderRadius: "16px", backgroundColor: "#ffffff" }}>
            <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
              <FaPiggyBank className="text-success" /> Rendimiento de Inversiones
            </h6>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="text-muted">Total Excedentes Colocados:</span>
              <span className="fw-bold text-success">S/ {financialInvestments.total.toLocaleString()}</span>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="text-muted">Tasa Efectiva Anual (TEA):</span>
              <span className="fw-bold text-dark">{financialInvestments.yieldRate}</span>
            </div>
            <small className="text-muted d-block mt-3">
              Generando S/ {financialInvestments.accumulatedYield.toLocaleString()} en rendimiento acumulado para este período.
            </small>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DashboardTesoreria;
